import { Link } from "react-router";
import { useEffect, useRef } from "react";
import SectionLabel from "../components/SectionLabel";
import ProjectArtwork from "../components/ProjectArtwork";
import { Arrow } from "../components/Icons";
import { projects, type Project } from "../data/projects";
import { track } from "../lib/analytics";

function ProjectLink({
  project,
  className = "",
}: {
  project: Project;
  className?: string;
}) {
  return (
    <article className={`project ${className}`}>
      <Link
        className="project-cover"
        to={`/work/${project.slug}`}
        onClick={() => track("project_select", { project: project.slug })}
        aria-label={`View ${project.title} case study`}
      >
        <ProjectArtwork project={project} />
        <span className="project-open">
          <Arrow diagonal />
        </span>
      </Link>
      <div className="project-info">
        <div>
          <p className="mono project-category">
            {project.number} / {project.category}
          </p>
          <h3>
            <Link to={`/work/${project.slug}`}>
              {project.title}
              <Arrow diagonal />
            </Link>
          </h3>
          <p className="project-description">{project.description}</p>
        </div>
        <div className="project-meta">
          <span className="mono">{project.status}</span>
          <span>{project.role}</span>
          <div className="tech-list">
            {project.technologies.slice(0, 3).map((tech) => (
              <span key={tech}>{tech}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="project-actions">
        <Link className="case-link mono" to={`/work/${project.slug}`}>
          View case study <Arrow />
        </Link>
        {project.liveUrl && (
          <a
            className="case-link demo-link mono"
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("live_site_click", { project: project.slug })}
          >
            View demo <Arrow diagonal />
            <span className="sr-only"> for {project.title} (opens in a new tab)</span>
          </a>
        )}
      </div>
    </article>
  );
}

export default function SelectedWork({
  standalone = false,
}: {
  standalone?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const Heading = standalone ? "h1" : "h2";
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          track("selected_work_view");
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <section
      id="work"
      className={`selected-work section container ${standalone ? "standalone-work" : ""}`}
      ref={ref}
    >
      <div className="section-heading">
        <SectionLabel index="01">Selected work</SectionLabel>
        <span className="index-label mono">
          <span>{String(projects.length).padStart(2, "0")}</span> projects
        </span>
        <div className="work-intro">
          <Heading>
            A few things
            <br />
            <span className="work-intro-accent">
              I’ve made.
              <svg
                className="work-intro-underline"
                viewBox="0 0 420 24"
                fill="none"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M4 16C97 3 277 2 416 10M55 21C167 12 310 12 384 16" />
              </svg>
            </span>
          </Heading>
          <div className="work-intro-copy">
            <p className="work-intro-lead">
              I like making things that feel good to use.
            </p>
            <p>
              Here’s a closer look at what I built, the choices I made, and what
              I learned along the way.
            </p>
          </div>
        </div>
      </div>
      <ProjectLink project={projects[0]} className="featured-project" />
      <div className="project-pair">
        <ProjectLink project={projects[1]} />
        <ProjectLink project={projects[2]} />
      </div>
      <ProjectLink project={projects[3]} className="wide-project" />
    </section>
  );
}
